'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { trackEvent } from '@/lib/analytics';
import { PERSONAS, type PersonaId } from '@/lib/personas';
import {
  defaultInputsForPersona,
  calculateWeeklyHours,
  hoursSaved,
  yearlySaved,
  toWorkWeeks,
  getLifestyleCards,
  getValueMessages,
  type FeedbackDepth,
  type WorkloadInputs,
} from '@/lib/calculatorFormulas';
import RoughAnnotation from '@/components/common/RoughAnnotation';
import RoughBorder from '@/components/common/RoughBorder';
import RoughSlider from '@/components/common/RoughSlider';
import RoughCard from '@/components/common/RoughCard';
import RoughProgressDots from '@/components/common/RoughProgressDots';
import { ScribbleExclamation } from '@/components/common/Scribbles';

const TOTAL_STEPS = 5;

/* ── Animated counter hook ─────────────────────────────────── */
function useAnimatedNumber(target: number, duration = 1200): number {
  const [value, setValue] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(from + (target - from) * eased);
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return value;
}

/* ── Step progress dots (Rough.js canvas) ──────────────────── */
const StepProgress: React.FC<{ current: number; total: number }> = ({ current, total }) => (
  <div className="value-calc__progress">
    <RoughProgressDots current={current} total={total} seed={50} />
    <p className="value-calc__progress-label">Step {current} of {total}</p>
  </div>
);

/* ── Slider input (Rough.js canvas) ────────────────────────── */
let sliderSeedCounter = 100;
const CalcSlider: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onChange: (v: number) => void;
}> = ({ label, value, min, max, step = 1, suffix = '', onChange }) => {
  const seedRef = useRef(sliderSeedCounter++);
  return (
    <div className="value-calc__slider-group">
      <div className="value-calc__slider-header">
        <label>{label}</label>
        <span className="value-calc__slider-value">{value}{suffix}</span>
      </div>
      <RoughSlider
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
        label={label}
        seed={seedRef.current}
      />
      <div className="value-calc__slider-range">
        <span>{min}{suffix}</span>
        <span>{max}{suffix}</span>
      </div>
    </div>
  );
};

/* ── Main component ────────────────────────────────────────── */
const ValueCalculator: React.FC = () => {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [persona, setPersona] = useState<PersonaId | null>(null);
  const [inputs, setInputs] = useState<WorkloadInputs>(defaultInputsForPersona('teacher'));
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const contentRef = useRef<HTMLDivElement>(null);

  // Computed results
  const weeklyHours = persona && persona !== 'student-parent' ? calculateWeeklyHours(inputs) : 0;
  const saved = hoursSaved(weeklyHours);
  const yearly = yearlySaved(saved);
  const workWeeks = toWorkWeeks(yearly);
  const lifestyleCards = getLifestyleCards(saved);
  const valueMessages = persona ? getValueMessages(persona) : [];

  const animatedHours = useAnimatedNumber(step === 3 ? weeklyHours : 0);
  const animatedSaved = useAnimatedNumber(step === 4 ? saved : 0);

  const goTo = useCallback((nextStep: number) => {
    setDirection(nextStep > step ? 'forward' : 'back');
    setStep(nextStep);
    contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step]);

  const selectPersona = (id: PersonaId) => {
    setPersona(id);
    setInputs(defaultInputsForPersona(id));
    trackEvent('calc_persona_selected', 'calculator', id);
    if (id === 'student-parent') {
      goTo(2); // goes to student-parent value props
    } else {
      goTo(2);
    }
  };

  const updateInput = <K extends keyof WorkloadInputs>(key: K, value: WorkloadInputs[K]) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleCTA = () => {
    trackEvent('calc_cta_clicked', 'conversion', persona || 'unknown');
    trackEvent('book_demo_click', 'CTA', 'value_calculator');
    const params = new URLSearchParams();
    if (persona) params.set('persona', persona);
    if (saved > 0) params.set('hours_saved', Math.round(saved).toString());
    router.push(`/book-demo?${params.toString()}`);
  };

  return (
    <section className="value-calc">
      <div className="rough-border-wrap value-calc__border-wrap">
        <RoughBorder color="#6b7a95" roughness={0.6} strokeWidth={1.2} seed={42} />
        <div className="value-calc__inner" ref={contentRef} style={{ position: 'relative', zIndex: 1 }}>
        <StepProgress current={step} total={persona === 'student-parent' ? 3 : TOTAL_STEPS} />

        {/* ── Step 1: Persona Picker ─────────────────────── */}
        {step === 1 && (
          <div className={`value-calc__step value-calc__step--${direction}`} key="step-1">
            <div className="value-calc__step-header">
              <span className="section-eyebrow">Let's find your value</span>
              <h1 className="value-calc__title">Who are you?</h1>
              <p className="value-calc__subtitle">We'll tailor the experience to your role.</p>
            </div>

            <div className="value-calc__persona-grid">
              {PERSONAS.map((p, i) => (
                <RoughCard
                  key={p.id}
                  active={persona === p.id}
                  onClick={() => selectPersona(p.id)}
                  seed={i * 7 + 200}
                  padding="28px 20px"
                  className="value-calc__persona-card"
                >
                  <span className="value-calc__persona-icon" aria-hidden="true">
                    {React.createElement(p.icon, { width: 28, height: 28 })}
                  </span>
                  <span className="value-calc__persona-label">{p.label}</span>
                  <span className="value-calc__persona-tagline">{p.tagline}</span>
                </RoughCard>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 2: Workload Inputs (graders) ──────────── */}
        {step === 2 && persona && persona !== 'student-parent' && (
          <div className={`value-calc__step value-calc__step--${direction}`} key="step-2">
            <div className="value-calc__step-header">
              <span className="section-eyebrow">Your workload</span>
              <h2 className="value-calc__title">Tell us about your grading</h2>
              <p className="value-calc__subtitle">Adjust the sliders to match your situation.</p>
            </div>

            <div className="value-calc__form">
              {(persona === 'center-owner' || persona === 'school-leader') && (
                <CalcSlider
                  label={persona === 'center-owner' ? 'Number of tutors' : 'Number of teachers'}
                  value={inputs.staffCount}
                  min={1}
                  max={persona === 'school-leader' ? 200 : 50}
                  onChange={(v) => updateInput('staffCount', v)}
                />
              )}

              <CalcSlider
                label="Papers graded per week (per grader)"
                value={inputs.papersPerWeek}
                min={5}
                max={100}
                step={5}
                onChange={(v) => updateInput('papersPerWeek', v)}
              />

              <CalcSlider
                label="Questions per paper"
                value={inputs.questionsPerPaper}
                min={5}
                max={30}
                onChange={(v) => updateInput('questionsPerPaper', v)}
              />

              <CalcSlider
                label="Math assignments"
                value={inputs.mathPct}
                min={0}
                max={100}
                step={10}
                suffix="%"
                onChange={(v) => {
                  const remaining = 100 - v;
                  updateInput('mathPct', v);
                  if (inputs.essayPct > remaining) updateInput('essayPct', remaining);
                }}
              />

              <CalcSlider
                label="Essay / long-form assignments"
                value={inputs.essayPct}
                min={0}
                max={100 - inputs.mathPct}
                step={10}
                suffix="%"
                onChange={(v) => updateInput('essayPct', v)}
              />

              <div className="value-calc__radio-group">
                <label className="value-calc__radio-label">How detailed is your feedback?</label>
                <div className="value-calc__radio-options">
                  {([
                    { value: 'grade-only' as FeedbackDepth, label: 'Grade only', desc: 'Just marks' },
                    { value: 'brief' as FeedbackDepth, label: 'Brief comments', desc: '1-2 sentences' },
                    { value: 'detailed' as FeedbackDepth, label: 'Detailed feedback', desc: 'Full explanations' },
                  ]).map((opt, i) => (
                    <RoughCard
                      key={opt.value}
                      active={inputs.feedbackDepth === opt.value}
                      onClick={() => updateInput('feedbackDepth', opt.value)}
                      seed={i * 11 + 300}
                      padding="12px 8px"
                      className="value-calc__radio-btn"
                    >
                      <span className="value-calc__radio-btn-label">{opt.label}</span>
                      <span className="value-calc__radio-btn-desc">{opt.desc}</span>
                    </RoughCard>
                  ))}
                </div>
              </div>

              {/* Live preview */}
              <RoughCard seed={400} padding="16px" className="value-calc__preview">
                Current estimate: <strong>{weeklyHours.toFixed(1)} hrs/week</strong> grading
                {inputs.staffCount > 1 && <span> (across {inputs.staffCount} staff)</span>}
              </RoughCard>
            </div>

            <div className="value-calc__actions">
              <button className="value-calc__back" onClick={() => goTo(1)}>&larr; Back</button>
              <button className="value-calc__next" onClick={() => { trackEvent('calc_step_completed', 'calculator', '2'); goTo(3); }}>
                See the impact &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2-alt: Student/Parent value props ──────── */}
        {step === 2 && persona === 'student-parent' && (
          <div className={`value-calc__step value-calc__step--${direction}`} key="step-2-alt">
            <div className="value-calc__step-header">
              <span className="section-eyebrow">For Students & Parents</span>
              <h2 className="value-calc__title">Feedback that helps you learn.</h2>
              <p className="value-calc__subtitle">IntelGrader gives students clarity after every assignment.</p>
            </div>

            <div className="value-calc__value-cards">
              {[
                { icon: '\u26A1', title: 'See where you stand instantly', body: 'No more waiting days for grades. Get results within minutes of submitting.' },
                { icon: '\u{1F3AF}', title: 'Get clear next steps', body: 'Every graded paper comes with specific guidance on what to practice next.' },
                { icon: '\u{1F4C8}', title: 'Track your progress over time', body: 'Watch your mastery grow across topics with visual progress dashboards.' },
                { icon: '\u{1F4E4}', title: 'Share results with parents', body: 'Parent-ready reports that show exactly what your child is learning.' },
              ].map((card, i) => (
                <RoughCard key={card.title} seed={i * 13 + 500} padding="20px" className="value-calc__value-card">
                  <span className="value-calc__value-card-icon">{card.icon}</span>
                  <div>
                    <h3>{card.title}</h3>
                    <p>{card.body}</p>
                  </div>
                </RoughCard>
              ))}
            </div>

            <div className="value-calc__actions">
              <button className="value-calc__back" onClick={() => goTo(1)}>&larr; Back</button>
              <button className="value-calc__next" onClick={() => goTo(3)}>
                Get started &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3 (student-parent): CTA ───────────────── */}
        {step === 3 && persona === 'student-parent' && (
          <div className={`value-calc__step value-calc__step--${direction}`} key="step-3-sp">
            <div className="value-calc__step-header">
              <h2 className="value-calc__title">Tell your school about IntelGrader</h2>
              <p className="value-calc__subtitle">Share with your teacher or school leader, or book a demo for your institution.</p>
            </div>

            <RoughCard seed={800} padding="40px 24px" className="value-calc__cta-box">
              <button className="btn btn-primary btn-lg" onClick={handleCTA}>
                Book a Demo
              </button>
              <p className="value-calc__cta-note">We'll reach out within 24 hours. No commitment.</p>
            </RoughCard>

            <button className="value-calc__back mx-auto mt-4" onClick={() => goTo(2)}>&larr; Back</button>
          </div>
        )}

        {/* ── Step 3: Pain reveal (graders) ──────────────── */}
        {step === 3 && persona && persona !== 'student-parent' && (
          <div className={`value-calc__step value-calc__step--${direction}`} key="step-3">
            <div className="value-calc__step-header">
              <span className="section-eyebrow">The reality</span>
              <h2 className="value-calc__title">Here's what grading costs you</h2>
            </div>

            <div className="value-calc__pain-reveal">
              <div className="value-calc__big-number">
                <RoughAnnotation type="circle" color="#e74c3c" strokeWidth={2} padding={12} animationDuration={1000} show={step === 3} delay={500}>
                  <span className="value-calc__big-number-value">{animatedHours.toFixed(1)}</span>
                </RoughAnnotation>
                <ScribbleExclamation className="value-calc__pain-doodle" aria-hidden="true" />
                <span className="value-calc__big-number-unit">hours / week</span>
              </div>
              <p className="value-calc__pain-text">
                That's <strong>{yearly.toFixed(0)} hours per year</strong> &mdash; more than <strong>{workWeeks} full work weeks</strong> spent grading.
              </p>
              <p className="value-calc__pain-stat">
                55% of educators are considering early retirement due to this workload.
              </p>
            </div>

            <div className="value-calc__actions">
              <button className="value-calc__back" onClick={() => goTo(2)}>&larr; Back</button>
              <button className="value-calc__next" onClick={() => { trackEvent('calc_result_viewed', 'calculator', Math.round(saved).toString()); goTo(4); }}>
                See what you get back &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Value reveal (graders) ─────────────── */}
        {step === 4 && persona && persona !== 'student-parent' && (
          <div className={`value-calc__step value-calc__step--${direction}`} key="step-4">
            <div className="value-calc__step-header">
              <span className="section-eyebrow">With IntelGrader</span>
              <h2 className="value-calc__title">
                Get <RoughAnnotation type="highlight" color="rgba(80,200,120,0.25)" strokeWidth={1} padding={4} show={step === 4} delay={500}><strong>{animatedSaved.toFixed(1)} hours</strong></RoughAnnotation> back every week.
              </h2>
            </div>

            <div className="value-calc__lifestyle">
              <p className="value-calc__lifestyle-intro">That's enough time to...</p>
              <div className="value-calc__lifestyle-grid">
                {lifestyleCards.map((card, i) => (
                  <RoughCard key={card.text} seed={i * 9 + 600} padding="14px 20px" className="value-calc__lifestyle-card">
                    <span className="value-calc__lifestyle-emoji">{card.emoji}</span>
                    <span>{card.text}</span>
                  </RoughCard>
                ))}
              </div>
            </div>

            {valueMessages.length > 0 && (
              <RoughCard seed={700} padding="24px" className="value-calc__beyond">
                <h3>Beyond time savings</h3>
                <ul>
                  {valueMessages.map((msg) => (
                    <li key={msg}>{msg}</li>
                  ))}
                </ul>
              </RoughCard>
            )}

            <div className="value-calc__actions">
              <button className="value-calc__back" onClick={() => goTo(3)}>&larr; Back</button>
              <button className="value-calc__next" onClick={() => goTo(5)}>
                Start your free trial &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── Step 5: CTA (graders) ──────────────────────── */}
        {step === 5 && persona && persona !== 'student-parent' && (
          <div className={`value-calc__step value-calc__step--${direction}`} key="step-5">
            <div className="value-calc__step-header">
              <h2 className="value-calc__title">Start your free month</h2>
              <p className="value-calc__subtitle">
                We'll get you set up within 24 hours. No credit card. No commitment.
              </p>
            </div>

            <RoughCard seed={810} padding="40px 24px" className="value-calc__cta-box">
              <div className="value-calc__cta-summary">
                <div className="value-calc__cta-stat">
                  <span className="value-calc__cta-stat-number">{Math.round(saved)}</span>
                  <span className="value-calc__cta-stat-label">hrs/week saved</span>
                </div>
                <div className="value-calc__cta-stat">
                  <span className="value-calc__cta-stat-number">{Math.round(yearly)}</span>
                  <span className="value-calc__cta-stat-label">hrs/year saved</span>
                </div>
              </div>

              <button className="btn btn-primary btn-lg" onClick={handleCTA}>
                Book a Demo
              </button>
              <p className="value-calc__cta-note">
                We'll reach out to schedule a walkthrough tailored to your needs.
              </p>
            </RoughCard>

            <button className="value-calc__back mx-auto mt-4" onClick={() => goTo(4)}>&larr; Back</button>
          </div>
        )}

        <button onClick={() => router.push('/')} className="btn btn-ghost mx-auto mt-8">
          &larr; Back to Home
        </button>
      </div>
      </div>
    </section>
  );
};

export default ValueCalculator;
